import {
    minDate,
    findClosestDate,
    isDateInRange,
    getSurrogateKeyFromContentfulEntry,
} from '@libs/core';
import type { ReactNode } from 'react';
import type { Entry } from '../types';

export function filterEntryByDate(
    entry: Entry | null,
    previewDate: Date
): {
    entry: Entry | null;
    closestDate: Date | null;
    previewMeta: ReactNode;
} {
    let closestDateInTree: Date | null = null;

    if (entry?.metadata && entry?.sys && entry?.fields) {
        Object.keys(entry.fields).forEach((entryFieldKey: string) => {
            let entryField = entry.fields?.[entryFieldKey];

            if (Array.isArray(entryField)) {
                entryField = (entryField as Entry[]).filter(subEntry => {
                    const result = filterEntryByDate(subEntry, previewDate);
                    closestDateInTree = minDate(
                        result.closestDate,
                        closestDateInTree
                    );
                    return result.entry;
                });
            } else if (typeof entryField === 'object' && entryField !== null) {
                const result = filterEntryByDate(
                    entryField as Entry,
                    previewDate
                );
                closestDateInTree = minDate(
                    result.closestDate,
                    closestDateInTree
                );
                entryField = result.entry;
            }

            if (entry.fields) {
                entry.fields[entryFieldKey] = entryField ?? {};
            }
        });
    }

    const startDateString = entry?.fields?.startDate as string;
    const endDateString = entry?.fields?.endDate as string;
    const closestDate = findClosestDate(previewDate, [
        startDateString,
        endDateString,
    ]);

    closestDateInTree = minDate(closestDate, closestDateInTree);
    const filteredEntry = isDateInRange(previewDate, [
        startDateString,
        endDateString,
    ])
        ? entry
        : null;

    /** @ts-expect-error -- Contentful types need to be improved in a separate ticket  */
    const entrySurrogateKey = getSurrogateKeyFromContentfulEntry(filteredEntry);

    return {
        closestDate: closestDateInTree,
        previewMeta: (
            <>
                {closestDateInTree && (
                    <meta
                        name="x-preview-date"
                        content={closestDateInTree.toUTCString()}
                    />
                )}
                {entrySurrogateKey && <meta content={entrySurrogateKey} />}
            </>
        ),
        entry: filteredEntry,
    };
}

export function filterEntriesByDate(
    entries: (Entry | null)[],
    previewDate: Date
): {
    entries: Entry[];
    closestDate: Date | null;
    previewMeta: ReactNode;
} {
    let closestDateInTree: Date | null = null;

    const filteredEntries = entries.filter(entry => {
        const result = filterEntryByDate(entry, previewDate);
        closestDateInTree = minDate(result.closestDate, closestDateInTree);
        return result.entry;
    }) as Entry[];

    return {
        closestDate: closestDateInTree,
        previewMeta: (
            <>
                {closestDateInTree && (
                    <meta
                        name="x-preview-date"
                        content={(closestDateInTree as Date).toUTCString()}
                    />
                )}

                {filteredEntries?.map(entry => {
                    const surrogateKey =
                        /** @ts-expect-error -- Contentful types need to be improved in a separate ticket  */
                        getSurrogateKeyFromContentfulEntry(entry);

                    return surrogateKey ? (
                        <meta key={surrogateKey} content={surrogateKey} />
                    ) : null;
                })}
            </>
        ),
        entries: filteredEntries,
    };
}
